<template>
  <div
    v-if="isVisible"
    class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/45 animate-fadeIn"
    @click="handleOverlayClick"
  >
    <div
      class="mx-auto w-full max-w-xs bg-white rounded-xl border border-gray-200 shadow-lg transition-all duration-200 animate-slideIn"
      @click.stop
    >
      <!-- 图标和标题 -->
      <div class="flex flex-col items-center pt-6 pb-4">
        <div
          class="flex justify-center items-center mb-3 w-12 h-12 rounded-full border border-gray-200"
          :class="getIconClass()"
        >
          <component :is="getIconComponent()" :size="20" color="white" />
        </div>
        <h3
          class="text-lg font-semibold tracking-tight text-center text-gray-900"
        >
          {{ title }}
        </h3>
      </div>

      <!-- 内容（超过高度时可滚动，最大高度 2/3 屏） -->
      <div class="px-6 pb-6">
        <div class="modal-body">
          <div
            class="text-sm leading-relaxed text-center text-gray-600 whitespace-pre-wrap"
          >
            <template v-for="(part, index) in parsedMessage" :key="index">
              <span v-if="part.type === 'text'">{{ part.content }}</span>
              <span
                v-else-if="part.type === 'bold'"
                class="font-bold text-gray-900"
                >{{ part.content }}</span
              >
            </template>
          </div>
        </div>
      </div>

      <!-- 按钮 -->
      <div class="flex gap-3 px-6 pb-6">
        <button
          class="flex-1 px-4 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-lg border border-gray-200 transition-colors duration-200"
          @click="handleCancel"
        >
          取消
        </button>
        <button
          class="flex-1 px-4 py-2.5 font-medium text-white rounded-lg shadow-sm transition-colors duration-200"
          :class="getConfirmButtonClass()"
          @click="handleConfirm"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { AlertTriangle, AlertCircle, Info } from 'lucide-vue-next'

interface Props {
  isVisible: boolean
  title: string
  message: string
  confirmText?: string
  type?: 'warning' | 'danger' | 'info'
}

interface Emits {
  (e: 'confirm'): void
  (e: 'cancel'): void
  (e: 'update:isVisible', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: '确定',
  type: 'warning'
})

const emit = defineEmits<Emits>()

// 解析消息，处理换行符、制表符和 <b> 标签
const parsedMessage = computed(() => {
  let text = props.message
    .replace(/\\n/g, '\n') // 处理 \n 换行符
    .replace(/\\t/g, '\t') // 处理 \t 制表符

  const parts: Array<{ type: 'text' | 'bold'; content: string }> = []

  // 使用正则表达式解析 <b></b> 标签
  const regex = /<b\s*>(.*?)<\/b>/gi
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    // 添加 <b> 标签前的普通文本
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index)
      if (beforeText) {
        parts.push({ type: 'text', content: beforeText })
      }
    }

    // 添加 <b> 标签内的加粗文本
    parts.push({ type: 'bold', content: match[1] })
    lastIndex = regex.lastIndex
  }

  // 添加最后剩余的普通文本
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex)
    if (remainingText) {
      parts.push({ type: 'text', content: remainingText })
    }
  }

  // 如果没有找到任何 <b> 标签，返回整个文本作为普通文本
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text })
  }

  return parts
})

const getIconComponent = () => {
  const iconMap = {
    warning: AlertTriangle,
    danger: AlertCircle,
    info: Info
  }
  return iconMap[props.type]
}

const getIconClass = () => {
  const classMap = {
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500'
  }
  return classMap[props.type]
}

const getConfirmButtonClass = () => {
  const classMap = {
    warning: 'bg-amber-600 hover:bg-amber-700',
    danger: 'bg-red-600 hover:bg-red-700',
    info: 'bg-blue-600 hover:bg-blue-700'
  }
  return classMap[props.type]
}

const handleConfirm = () => {
  emit('confirm')
  emit('update:isVisible', false)
}

const handleCancel = () => {
  emit('cancel')
  emit('update:isVisible', false)
}

const handleOverlayClick = () => {
  emit('cancel')
  emit('update:isVisible', false)
}

// 监听ESC键
watch(
  () => props.isVisible,
  visible => {
    if (visible) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleCancel()
        }
      }
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }
)
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 动画效果类（统一时长与缓动） */
.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-slideIn {
  animation: slideIn 0.2s ease-out;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .modal-actions {
    flex-direction: column;
    gap: 12px;
  }
}

/* 优化按钮触摸体验（扁平风格不上浮） */
@media (hover: hover) {
  button:hover {
    transform: none;
  }
}

/* 内容最大高度与滚动支持 */
.modal-body {
  max-height: 66vh; /* 不超过屏幕的2/3 */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* iOS 平滑滚动 */
}

/* HTML 内容样式 */
.modal-body :deep(b) {
  font-weight: 700;
  color: #111827; /* text-gray-900 */
}

.modal-body :deep(br) {
  line-height: 1.5;
}

/* 高对比度模式支持 */
@media (prefers-contrast: high) {
  .bg-white {
    background-color: white;
  }
}
</style>
